import { css } from '@emotion/react'
import React, { useState } from 'react'

import { Modal, ModalHeader, ModalBody, ModalFooter } from '../../../shared/modal'
import { ModalSize } from '../../../shared/modal/Modal/Modal'
import useAppSettings from '../../../../lib/hooks/useAppSettings'
import { PrimaryButton, SecondaryButton } from '../../../shared/buttons'
import { ButtonType } from '../../../shared/buttons/types'
import { subtitle1Css, tertiaryBodyCss } from '../../../../lib/styles/typography'
import { Checkbox } from '../../../shared/input'
import { CompletedCoursesData } from '../../../../types'
import { CompletedCoursePreference } from '../../../../generated/graphql'
import { tableCellCss, tableCss } from '../../../../lib/styles/tableStyles'

type ScheduleCompletedCoursesModalProps = {
  /** Contains information about the active term(s) and completed items */
  completedCoursesData: Array<CompletedCoursesData>
  /** Indicates if the modal is open */
  isOpen: boolean
  /** Event callback when user triggers a close event (eg. button click or ESC key) */
  onCancel: () => void
  /** Event callback when the form is submitted */
  onSubmit: (completedCourses: CompletedCoursePreference[]) => void
}

type CompletedCourseState = {
  courseNumber: string
  groupTitle: string
  id: string
  include: boolean | null
  planId: string
  subjectId: string
  termCode: string
}

const upperFormCss = css`
  margin-top: -10px;

  b {
    font-weight: 500;
  }
`

const tableContainerCss = css`
  ${tableCss}
  margin-top: 20px;
`

const headerCss = css`
  ${subtitle1Css}
  padding: 12px;
  text-align: left;
  vertical-align: bottom;
  ${tertiaryBodyCss}
`

const ScheduleCompletedCoursesModal = ({
  completedCoursesData,
  isOpen,
  onCancel,
  onSubmit
}: ScheduleCompletedCoursesModalProps) => {
  const { text } = useAppSettings()
  const courseLabel = text.course
  const coursePluralLabel = text.coursePlural
  const scheduleLabel = text.schedule
  const termLabel = text.term

  // flatten the data, and store it in state
  const flattenedCompletedCourses: CompletedCourseState[] = []
  completedCoursesData.forEach((group) => {
    const flattenedGroupData = group.completedItems.map((item) => ({
      id: item.id,
      courseNumber: item.courseNumber || '',
      groupTitle: group.groupTitle,
      planId: group.planId,
      include: true,
      subjectId: item.subjectId || '',
      termCode: group.termCode || ''
    }))
    flattenedCompletedCourses.push(...flattenedGroupData)
  })
  const [completedCourses, setCompletedCourses] =
    useState<CompletedCourseState[]>(flattenedCompletedCourses)

  // When the user checks the checkbox, update the 'include' flag for that item in state
  const onChangeEvent = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompletedCourses(
      completedCourses.map((completedCourse) =>
        completedCourse.id === e.target.id
          ? { ...completedCourse, include: !completedCourse.include }
          : completedCourse
      )
    )
  }

  const onSubmitModal = (e: React.FormEvent) => {
    e.preventDefault()
    // Map the state objects into the CompletedCoursePreference object
    const completedCoursePreferences = completedCourses.map((item) => ({
      courseNumber: item.courseNumber,
      planId: item.planId,
      schedulerInclude: item.include,
      subject: item.subjectId,
      termCode: item.termCode
    }))
    onSubmit(completedCoursePreferences)
  }

  // For each term, build up the phrase like '2 courses in your 2021 Fall plan'
  const courseTermDescription = completedCoursesData.map(
    (data) => `
    <b>${data.completedItems.length}
    ${
      data.completedItems.length === 1 ? courseLabel.toLowerCase() : coursePluralLabel.toLowerCase()
    }</b> in your <b>${data.groupTitle}</b> plan`
  )
  // Determine the correct wording based on how many courses there are
  const hasOrHave = completedCourses.length === 1 ? 'has' : 'have'
  const theseCoursesOrThisCourse =
    completedCourses.length === 1
      ? `this  ${courseLabel.toLowerCase()}`
      : `these  ${coursePluralLabel.toLowerCase()}`
  // Build up the full description
  const fullDescription = `You have ${courseTermDescription.join(' and ')}
    that ${hasOrHave} already been completed.  Are you sure you want to create a ${scheduleLabel.toLowerCase()} with ${theseCoursesOrThisCourse}?`

  return (
    <Modal allowMobilePage isOpen={isOpen} size={ModalSize.SM} onSubmit={onSubmitModal}>
      <ModalHeader
        allowMobile
        onClose={onCancel}
        title={`Confirm ${courseLabel.toLowerCase()} selections`}
      />
      <ModalBody allowMobile>
        <div css={[tertiaryBodyCss, upperFormCss]}>
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: fullDescription
            }}
          />
        </div>
        <table css={tableContainerCss}>
          <thead css={headerCss}>
            <th>{courseLabel} ID</th>
            <th>{termLabel}</th>
            <th>Include</th>
          </thead>
          <tbody>
            {completedCourses.map((item) => (
              <tr key={`row-${item.id}`} css={tableCellCss}>
                <td>{`${item.subjectId} ${item.courseNumber}`}</td>
                <td>{item.groupTitle}</td>
                <td>
                  <Checkbox
                    ariaLabel={`${item.include ? 'Include' : 'Exclude'} ${courseLabel} ${
                      item.subjectId
                    } ${item.courseNumber}`}
                    checked={item.include!}
                    id={item.id}
                    onChange={onChangeEvent}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ModalBody>
      <ModalFooter>
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
        <PrimaryButton type={ButtonType.Submit}>Continue</PrimaryButton>
      </ModalFooter>
    </Modal>
  )
}

export default ScheduleCompletedCoursesModal
